import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, Package, ShieldAlert, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { apqpService } from '../../services/api';

const OUTCOMES = ['In corso', 'Positivo', 'Positivo con deroga', 'Negativo'];

const OUTCOME_META = {
  'In corso':             { color: 'bg-slate-100 text-slate-700 border-slate-300', icon: Clock },
  'Positivo':             { color: 'bg-emerald-100 text-emerald-800 border-emerald-300', icon: CheckCircle2 },
  'Positivo con deroga':  { color: 'bg-amber-100 text-amber-800 border-amber-300', icon: ShieldAlert },
  'Negativo':             { color: 'bg-red-100 text-red-800 border-red-300', icon: XCircle },
};

const defaultForm = () => ({
  produced_quantity: '', conforming_quantity: '', non_conforming_quantity: '',
  production_date: '', control_date: '', shipment_date: '',
  ddt_number: '', outcome: 'In corso', deroga_notes: '', notes: '',
});

export default function ApqpSamplingsTab({ projectId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dlg, setDlg] = useState({ open: false, editing: null, form: defaultForm() });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apqpService.listSamplings(projectId);
      setItems(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore caricamento campionature');
    } finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => setDlg({ open: true, editing: null, form: defaultForm() });
  const openEdit = (s) => setDlg({
    open: true, editing: s.id,
    form: {
      produced_quantity: s.produced_quantity ?? '',
      conforming_quantity: s.conforming_quantity ?? '',
      non_conforming_quantity: s.non_conforming_quantity ?? '',
      production_date: s.production_date || '',
      control_date: s.control_date || '',
      shipment_date: s.shipment_date || '',
      ddt_number: s.ddt_number || '',
      outcome: s.outcome || 'In corso',
      deroga_notes: s.deroga_notes || '',
      notes: s.notes || '',
    },
  });

  const save = async () => {
    const { editing, form } = dlg;
    const payload = { ...form };
    ['produced_quantity', 'conforming_quantity', 'non_conforming_quantity'].forEach(k => {
      payload[k] = payload[k] === '' ? null : Number(payload[k]);
    });
    ['production_date', 'control_date', 'shipment_date'].forEach(k => {
      if (payload[k] === '') payload[k] = null;
    });
    try {
      if (editing) {
        await apqpService.updateSampling(editing, payload);
        toast.success('Campionatura aggiornata');
      } else {
        await apqpService.createSampling(projectId, payload);
        toast.success('Campionatura registrata');
      }
      setDlg(d => ({ ...d, open: false }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore salvataggio campionatura');
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Eliminare la campionatura ${s.number}?`)) return;
    try {
      await apqpService.deleteSampling(s.id);
      toast.success('Campionatura eliminata');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore eliminazione');
    }
  };

  const hasDeroga = dlg.form.outcome === 'Positivo con deroga';

  return (
    <div className="space-y-3" data-testid="apqp-samplings-tab">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-500 flex items-center gap-1.5">
          <Package className="w-4 h-4" /> Registro campionature del progetto — {items.length} record
        </div>
        <Button size="sm" onClick={openNew} data-testid="new-sampling-btn"><Plus className="w-4 h-4 mr-1" /> Nuova campionatura</Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b">
                <tr className="text-left">
                  <th className="px-2 py-2 font-medium">N.</th>
                  <th className="px-2 py-2 font-medium">Prodotta</th>
                  <th className="px-2 py-2 font-medium">Conforme</th>
                  <th className="px-2 py-2 font-medium">Non conforme</th>
                  <th className="px-2 py-2 font-medium">Produzione</th>
                  <th className="px-2 py-2 font-medium">Controllo</th>
                  <th className="px-2 py-2 font-medium">Spedizione</th>
                  <th className="px-2 py-2 font-medium">DDT</th>
                  <th className="px-2 py-2 font-medium">Esito</th>
                  <th className="px-2 py-2 font-medium">Deroga</th>
                  <th></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {loading && items.length === 0 && (
                  <tr><td colSpan={11} className="text-center text-slate-400 py-6">Caricamento…</td></tr>
                )}
                {!loading && items.length === 0 && (
                  <tr><td colSpan={11} className="text-center text-slate-400 py-8" data-testid="samplings-empty">Nessuna campionatura registrata per questo progetto.</td></tr>
                )}
                {items.map(s => {
                  const meta = OUTCOME_META[s.outcome] || OUTCOME_META['In corso'];
                  const Icon = meta.icon;
                  return (
                    <tr key={s.id} data-testid={`sampling-row-${s.id}`}>
                      <td className="px-2 py-1.5 font-mono font-semibold">{s.number}</td>
                      <td className="px-2 py-1.5 font-mono">{s.produced_quantity ?? '—'}</td>
                      <td className="px-2 py-1.5 font-mono text-emerald-700">{s.conforming_quantity ?? '—'}</td>
                      <td className="px-2 py-1.5 font-mono text-red-700">{s.non_conforming_quantity ?? '—'}</td>
                      <td className="px-2 py-1.5 font-mono">{s.production_date || '—'}</td>
                      <td className="px-2 py-1.5 font-mono">{s.control_date || '—'}</td>
                      <td className="px-2 py-1.5 font-mono">{s.shipment_date || '—'}</td>
                      <td className="px-2 py-1.5 font-mono">{s.ddt_number || '—'}</td>
                      <td className="px-2 py-1.5">
                        <Badge className={`${meta.color} border text-[10px]`}>
                          <Icon className="w-3 h-3 mr-1 inline" />{s.outcome}
                        </Badge>
                      </td>
                      <td className="px-2 py-1.5 text-slate-600 max-w-[220px] truncate" title={s.deroga_notes || ''}>{s.deroga_notes || '—'}</td>
                      <td className="px-2 py-1.5 text-right whitespace-nowrap">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(s)} data-testid={`edit-sampling-${s.id}`}><Edit2 className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => remove(s)}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={dlg.open} onOpenChange={(o) => !o && setDlg(d => ({ ...d, open: false }))}>
        <DialogContent className="max-w-2xl" data-testid="apqp-sampling-dialog">
          <DialogHeader>
            <DialogTitle>{dlg.editing ? 'Modifica campionatura' : 'Nuova campionatura'}</DialogTitle>
            <DialogDescription>Registra i dati di produzione e controllo. Nel caso di deroga, indicare le note.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><Label>Q.tà prodotta</Label><Input type="number" value={dlg.form.produced_quantity} onChange={e => setDlg(d => ({ ...d, form: { ...d.form, produced_quantity: e.target.value } }))} data-testid="sampling-produced-input" /></div>
            <div><Label>Q.tà conforme</Label><Input type="number" value={dlg.form.conforming_quantity} onChange={e => setDlg(d => ({ ...d, form: { ...d.form, conforming_quantity: e.target.value } }))} /></div>
            <div><Label>Q.tà non conforme</Label><Input type="number" value={dlg.form.non_conforming_quantity} onChange={e => setDlg(d => ({ ...d, form: { ...d.form, non_conforming_quantity: e.target.value } }))} /></div>
            <div><Label>Data produzione</Label><Input type="date" value={dlg.form.production_date} onChange={e => setDlg(d => ({ ...d, form: { ...d.form, production_date: e.target.value } }))} /></div>
            <div><Label>Data controllo</Label><Input type="date" value={dlg.form.control_date} onChange={e => setDlg(d => ({ ...d, form: { ...d.form, control_date: e.target.value } }))} /></div>
            <div><Label>Data spedizione</Label><Input type="date" value={dlg.form.shipment_date} onChange={e => setDlg(d => ({ ...d, form: { ...d.form, shipment_date: e.target.value } }))} /></div>
            <div><Label>N. DDT</Label><Input value={dlg.form.ddt_number} onChange={e => setDlg(d => ({ ...d, form: { ...d.form, ddt_number: e.target.value } }))} data-testid="sampling-ddt-input" /></div>
            <div className="md:col-span-2">
              <Label>Esito</Label>
              <Select value={dlg.form.outcome} onValueChange={(v) => setDlg(d => ({ ...d, form: { ...d.form, outcome: v } }))}>
                <SelectTrigger data-testid="sampling-outcome-select"><SelectValue /></SelectTrigger>
                <SelectContent>{OUTCOMES.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {hasDeroga && (
              <div className="md:col-span-3">
                <Label>Note deroga</Label>
                <Input value={dlg.form.deroga_notes} onChange={e => setDlg(d => ({ ...d, form: { ...d.form, deroga_notes: e.target.value } }))} data-testid="sampling-deroga-input" />
                <p className="text-xs text-amber-600 mt-1">Motivazione della deroga concessa dal cliente.</p>
              </div>
            )}
            <div className="md:col-span-3">
              <Label>Note</Label>
              <Input value={dlg.form.notes} onChange={e => setDlg(d => ({ ...d, form: { ...d.form, notes: e.target.value } }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(d => ({ ...d, open: false }))}>Annulla</Button>
            <Button onClick={save} data-testid="save-sampling-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
